import { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { ChevronLeftIcon, ChevronRightIcon, EllipsisVerticalIcon } from '@heroicons/react/24/outline';

const MENU_WIDTH = 288;
const VIEWPORT_GAP = 8;

const menuPosition = (buttonRect, itemCount) => {
  const width = Math.min(MENU_WIDTH, window.innerWidth - (VIEWPORT_GAP * 2));
  const estimatedHeight = Math.min((itemCount * 44) + 16, window.innerHeight - 16);
  const spaceBelow = window.innerHeight - buttonRect.bottom - VIEWPORT_GAP;
  const openAbove = spaceBelow < estimatedHeight && buttonRect.top > spaceBelow;
  return {
    width,
    left: Math.min(
      Math.max(VIEWPORT_GAP, buttonRect.right - width),
      window.innerWidth - width - VIEWPORT_GAP
    ),
    ...(openAbove
      ? { bottom: window.innerHeight - buttonRect.top + 4 }
      : { top: buttonRect.bottom + 4 }),
  };
};

const RecordActionsMenu = ({ actions, studentName, onSelect }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [position, setPosition] = useState(null);
  const [parentAction, setParentAction] = useState(null);
  const triggerRef = useRef(null);
  const menuRef = useRef(null);

  const closeMenu = useCallback(() => {
    setIsOpen(false);
    setPosition(null);
    setParentAction(null);
  }, []);

  const toggleMenu = event => {
    event.stopPropagation();
    if (isOpen) {
      closeMenu();
      return;
    }
    setPosition(menuPosition(triggerRef.current.getBoundingClientRect(), actions.length));
    setIsOpen(true);
  };

  useEffect(() => {
    if (!isOpen) return undefined;
    const handlePointerDown = event => {
      if (!triggerRef.current?.contains(event.target) && !menuRef.current?.contains(event.target)) {
        closeMenu();
      }
    };
    const handleKeyDown = event => {
      if (event.key === 'Escape') {
        if (parentAction) {
          setParentAction(null);
        } else {
          closeMenu();
          triggerRef.current?.focus();
        }
      }
    };
    document.addEventListener('pointerdown', handlePointerDown);
    document.addEventListener('keydown', handleKeyDown);
    window.addEventListener('resize', closeMenu);
    window.addEventListener('scroll', closeMenu, true);
    return () => {
      document.removeEventListener('pointerdown', handlePointerDown);
      document.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('resize', closeMenu);
      window.removeEventListener('scroll', closeMenu, true);
    };
  }, [closeMenu, isOpen, parentAction]);

  if (!actions?.length) return null;

  const visibleActions = parentAction?.children || actions;
  const firstDangerIndex = visibleActions.findIndex(action => action.danger);

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        className="btn btn-sm btn-ghost btn-square"
        aria-label="More actions"
        aria-haspopup="menu"
        aria-expanded={isOpen}
        onClick={toggleMenu}
      >
        <EllipsisVerticalIcon className="h-5 w-5" />
      </button>
      {isOpen && position && createPortal(
        <ul
          ref={menuRef}
          role="menu"
          aria-label={parentAction?.label || `More actions for ${studentName}`}
          className="menu fixed z-[100] max-h-[calc(100vh-1rem)] overflow-y-auto rounded-box border border-base-300 bg-base-100 p-2 shadow-lg"
          style={position}
        >
          {parentAction && (
            <li role="none">
              <button
                type="button"
                role="menuitem"
                onClick={(event) => {
                  event.stopPropagation();
                  setParentAction(null);
                }}
              >
                <ChevronLeftIcon className="h-4 w-4" />
                Back
              </button>
            </li>
          )}
          {visibleActions.map((action, index) => {
            const ActionIcon = action.icon;
            return (
              <li key={action.id} role="none" className={index === firstDangerIndex && index > 0 ? 'mt-1 border-t border-base-300 pt-1' : ''}>
                <button
                  type="button"
                  role="menuitem"
                  title={action.description}
                  className={action.danger ? 'text-error' : ''}
                  onClick={(event) => {
                    event.stopPropagation();
                    if (action.children?.length) {
                      setParentAction(action);
                      return;
                    }
                    closeMenu();
                    onSelect(action);
                  }}
                >
                  {ActionIcon && <ActionIcon className="h-4 w-4" />}
                  <span className="flex-1 text-left">{action.label}</span>
                  {action.children?.length > 0 && <ChevronRightIcon className="h-4 w-4" />}
                </button>
              </li>
            );
          })}
        </ul>,
        document.body
      )}
    </>
  );
};

export default RecordActionsMenu;
